import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseInstant } from "../../utils/dateUtils";
import { subscriptionsService } from "../../features/subscriptions/service";
import { buildFeatureRows, type PlanFeatureRow } from "../../features/subscriptions/planFeatures";
import type { CurrenciesResponse, FeatureCatalogEntry, TierDiscount, TierDiscountRequest, TierLimitsResponse, TierPricing } from "../../features/subscriptions/types";
import FeatureInfoTooltip from "../../components/common/FeatureInfoTooltip";
import FeatureCatalogEditor from "../../components/admin/FeatureCatalogEditor";
import RefreshButton from "../../components/admin/RefreshButton";
import Spinner from "../../components/ui/Spinner";

const SELLER_TIERS = [
  {
    key: "free",
    label: "Free",
    badge: "bg-gray-700 text-gray-300",
    border: "border-gray-700",
    ring: "",
    tagline: "Entry-level, no payment needed",
  },
  {
    key: "starter",
    label: "Starter",
    badge: "bg-blue-900 text-blue-200",
    border: "border-blue-700",
    ring: "",
    tagline: "Individual agents & small agencies",
  },
  {
    key: "mid",
    label: "Mid",
    badge: "bg-indigo-900 text-indigo-200",
    border: "border-indigo-500",
    ring: "ring-2 ring-indigo-500/30",
    tagline: "Best for growing travel companies",
    popular: true,
  },
  {
    key: "premium",
    label: "Premium",
    badge: "bg-purple-900 text-purple-200",
    border: "border-purple-600",
    ring: "",
    tagline: "Large DMCs, hotel chains & enterprises",
  },
];

const BUYER_TIERS = [
  {
    key: "free",
    label: "Free",
    badge: "bg-gray-700 text-gray-300",
    border: "border-gray-700",
    ring: "",
    tagline: "Browse and discover travel deals",
  },
  {
    key: "pro",
    label: "Pro",
    badge: "bg-teal-900 text-teal-200",
    border: "border-teal-600",
    ring: "",
    tagline: "For frequent travel buyers",
  },
  {
    key: "studio",
    label: "Studio",
    badge: "bg-cyan-900 text-cyan-200",
    border: "border-cyan-500",
    ring: "ring-2 ring-cyan-500/30",
    tagline: "Travel agencies buying at scale",
    popular: true,
  },
  {
    key: "elite",
    label: "Elite",
    badge: "bg-amber-900 text-amber-200",
    border: "border-amber-500",
    ring: "",
    tagline: "High-volume B2B travel buyers",
  },
];

type TierMeta = (typeof SELLER_TIERS)[0];
type TierWithFeatures = TierMeta & { features: PlanFeatureRow[] };

interface DiscountDraft {
  discount_type: "percent" | "flat";
  value: string;
  starts_at: string;
  ends_at: string;
  active: boolean;
}

interface TierCardProps {
  tier: TierWithFeatures;
  priceCredits?: number;
  pricing?: TierPricing;
  currencySymbol: string;
  editable: boolean;
  editing: boolean;
  draftValue: string;
  onStartEdit: () => void;
  onChangeDraft: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  // Discount (money price) editing — separate feature from the credits-price
  // editing above. Do not merge: money price + discount is what buyers see
  // on the checkout page; credits price is a different, wallet-facing number.
  discountEditing: boolean;
  discountDraft: DiscountDraft;
  onStartDiscountEdit: () => void;
  onChangeDiscountDraft: (patch: Partial<DiscountDraft>) => void;
  onSaveDiscount: () => void;
  onCancelDiscount: () => void;
  onRemoveDiscount: () => void;
  savingDiscount: boolean;
  discountError: string;
}

function discountLabel(d: TierDiscount, symbol: string): string {
  return d.type === "percent" ? `${d.value}% off` : `${symbol}${d.value.toLocaleString()} off`;
}

function TierCard({
  tier, priceCredits, pricing, currencySymbol, editable, editing, draftValue, onStartEdit, onChangeDraft, onSave, onCancel, saving,
  discountEditing, discountDraft, onChangeDiscountDraft, onSaveDiscount, onCancelDiscount, savingDiscount, discountError,
}: TierCardProps) {
  const discounted = !!pricing?.discount && pricing.final_price < pricing.list_price;
  return (
    <div className={`relative flex flex-col bg-gray-900 rounded-2xl border-2 ${tier.border} ${tier.ring} p-5`}>
      {(tier as any).popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-indigo-600 rounded-full text-xs font-semibold text-white whitespace-nowrap">
          Most Popular
        </div>
      )}
      <div className="mb-4">
        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-bold ${tier.badge}`}>{tier.label}</span>
        <p className="text-gray-500 text-xs mt-2">{tier.tagline}</p>
        {editable && (
          <div className="mt-3 flex items-center gap-2">
            {editing ? (
              <>
                <input
                  type="number"
                  min={1}
                  value={draftValue}
                  onChange={(e) => onChangeDraft(e.target.value)}
                  className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  autoFocus
                />
                <span className="text-gray-500 text-xs">credits/mo</span>
                <button onClick={onSave} disabled={saving} className="text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50">
                  {saving ? "…" : "Save"}
                </button>
                <button onClick={onCancel} className="text-xs px-2 py-1 rounded-md text-gray-400 hover:text-white">Cancel</button>
              </>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {discounted && (
                    <span className="text-base text-gray-500 line-through">
                      {currencySymbol}{pricing!.list_price.toLocaleString()}
                    </span>
                  )}
                  <span className="text-2xl font-bold text-white">
                    {currencySymbol}{(pricing?.final_price ?? 0).toLocaleString()}
                  </span>
                  <span className="text-gray-500 text-xs">/month</span>
                  {discounted && (
                    <span className="px-1.5 py-0.5 rounded-md bg-emerald-900/50 text-emerald-300 text-[10px] font-semibold">
                      {discountLabel(pricing!.discount!, currencySymbol)}
                    </span>
                  )}
                </div>

                {/* Tier discounts here were display-only (never charged). Real,
                    charged discounts live on Billing → Coupons & Discounts. */}
                {!discountEditing && (
                  <p className="text-[11px] text-gray-600">
                    Prices: Billing → Plans &amp; Prices · Discounts: Billing → Coupons &amp; Discounts
                  </p>
                )}
                {discountEditing && (
                  <div className="flex flex-col gap-1.5 p-2 bg-gray-800/60 border border-gray-700 rounded-lg">
                    <div className="flex items-center gap-1.5">
                      <select
                        value={discountDraft.discount_type}
                        onChange={(e) => onChangeDiscountDraft({ discount_type: e.target.value as "percent" | "flat" })}
                        className="bg-gray-800 border border-gray-700 rounded-md px-1.5 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      >
                        <option value="percent">% off</option>
                        <option value="flat">{currencySymbol} off</option>
                      </select>
                      <input
                        type="number"
                        min={0}
                        max={discountDraft.discount_type === "percent" ? 100 : undefined}
                        value={discountDraft.value}
                        onChange={(e) => onChangeDiscountDraft({ value: e.target.value })}
                        placeholder="value"
                        className="w-16 bg-gray-800 border border-gray-700 rounded-md px-1.5 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label className="text-[10px] text-gray-500 flex flex-col gap-0.5">
                        Starts
                        <input
                          type="date"
                          value={discountDraft.starts_at}
                          onChange={(e) => onChangeDiscountDraft({ starts_at: e.target.value })}
                          className="bg-gray-800 border border-gray-700 rounded-md px-1 py-0.5 text-white text-[11px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </label>
                      <label className="text-[10px] text-gray-500 flex flex-col gap-0.5">
                        Ends
                        <input
                          type="date"
                          value={discountDraft.ends_at}
                          onChange={(e) => onChangeDiscountDraft({ ends_at: e.target.value })}
                          className="bg-gray-800 border border-gray-700 rounded-md px-1 py-0.5 text-white text-[11px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </label>
                      <label className="text-[10px] text-gray-500 flex items-center gap-1 mt-3">
                        <input
                          type="checkbox"
                          checked={discountDraft.active}
                          onChange={(e) => onChangeDiscountDraft({ active: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                    {discountError && <span className="text-[10px] text-red-400">{discountError}</span>}
                    <div className="flex items-center gap-2">
                      <button onClick={onSaveDiscount} disabled={savingDiscount} className="text-xs px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50">
                        {savingDiscount ? "…" : "Save"}
                      </button>
                      <button onClick={onCancelDiscount} className="text-xs px-2 py-1 rounded-md text-gray-400 hover:text-white">Cancel</button>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">
                    ≈ {(pricing?.credits_equivalent ?? priceCredits ?? 0).toLocaleString()} credits/mo
                  </span>
                  <button onClick={onStartEdit} className="text-xs px-2 py-0.5 rounded-md text-indigo-400 hover:bg-indigo-500/10">Edit credits price</button>
                </div>
              </div>
            )}
          </div>
        )}
        {!editable && (
          <div className="mt-3">
            <span className="text-2xl font-bold text-white">Free</span>
          </div>
        )}
      </div>
      <ul className="space-y-2 flex-1">
        {tier.features.map((f) => (
          <li key={f.label} className="flex items-center justify-between text-sm gap-2">
            <span className="text-gray-500 flex items-center gap-1.5 shrink-0">
              <span>{f.icon}</span>
              <span className="text-xs">{f.label}</span>
            </span>
            <span className={`text-xs font-medium text-right ${f.dim ? "text-gray-600 line-through" : f.green ? "text-green-400" : "text-white"}`}>
              {f.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminPricingPage() {
  const [initialLoading, setInitialLoading] = useState(true);
  const [role, setRole] = useState<"seller" | "buyer">("seller");
  const [tierLimits, setTierLimits] = useState<TierLimitsResponse | null>(null);
  const [currencies, setCurrencies] = useState<CurrenciesResponse | null>(null);
  const [featureCatalog, setFeatureCatalog] = useState<FeatureCatalogEntry[]>([]);
  const [featureCatalogByAction, setFeatureCatalogByAction] = useState<Record<string, FeatureCatalogEntry>>({});
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Discount (money price) editing state — independent of the credits-price
  // editing above.
  const [discountEditingTier, setDiscountEditingTier] = useState<string | null>(null);
  const [discountDraft, setDiscountDraft] = useState<DiscountDraft>({
    discount_type: "percent", value: "", starts_at: "", ends_at: "", active: true,
  });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState("");

  const activeCurrencySymbol =
    currencies?.currencies.find((c) => c.code === (tierLimits?.currency ?? currencies?.active))?.symbol ?? "";
  const [savingCurrency, setSavingCurrency] = useState(false);

  // Credit rate
  const [creditRate, setCreditRate] = useState<{ rate_paise: number; rate_inr: number; updated_at: string | null } | null>(null);
  const [editingCreditRate, setEditingCreditRate] = useState(false);
  const [creditRateDraft, setCreditRateDraft] = useState("");
  const [savingCreditRate, setSavingCreditRate] = useState(false);
  const [creditRateError, setCreditRateError] = useState("");

  // Per-generation credit cost (flyer / video / itinerary) — what a caller is
  // charged from the wallet to generate one past their quota.
  const [genCosts, setGenCosts] = useState<{ costs_credits: Record<string, number>; rate_paise: number; costs_inr: Record<string, number> } | null>(null);
  const [editingGenAction, setEditingGenAction] = useState<string | null>(null);
  const [genDraft, setGenDraft] = useState("");
  const [savingGen, setSavingGen] = useState(false);
  const [genError, setGenError] = useState("");

  const refreshGenCosts = useCallback(() => {
    return subscriptionsService.adminGetGenerationCosts()
      .then((data) => setGenCosts(data))
      .catch(() => {});
  }, []);

  const handleSaveGenCost = async (action: "flyer" | "video" | "itinerary") => {
    const credits = Math.round(Number(genDraft));
    if (!Number.isFinite(credits) || credits < 1) {
      setGenError("Enter a whole number of credits (≥ 1)");
      return;
    }
    setSavingGen(true);
    setGenError("");
    try {
      await subscriptionsService.adminSetGenerationCost(action, credits);
      setEditingGenAction(null);
      refreshGenCosts();
    } catch {
      setGenError("Failed to save. Try again.");
    } finally {
      setSavingGen(false);
    }
  };

  const refresh = useCallback(() => {
    return subscriptionsService
      .getTierLimits(role)
      .then((data) => setTierLimits(data))
      .catch(() => setTierLimits(null));
  }, [role]);

  const refreshFeatureCatalog = useCallback(() => {
    return subscriptionsService
      .adminGetFeatureCatalog()
      .then((data) => {
        setFeatureCatalog(data.features);
        const byAction: Record<string, FeatureCatalogEntry> = {};
        for (const f of data.features) byAction[f.action] = f;
        setFeatureCatalogByAction(byAction);
      })
      .catch(() => {
        setFeatureCatalog([]);
        setFeatureCatalogByAction({});
      });
  }, []);

  const fetchCreditRate = useCallback(() => {
    return subscriptionsService.adminGetCreditRate()
      .then((data) => setCreditRate(data))
      .catch(() => {});
  }, []);

  const loadInitialSettings = useCallback(() => {
    return Promise.all([refreshFeatureCatalog(), fetchCreditRate(), refreshGenCosts()]);
  }, [refreshFeatureCatalog, fetchCreditRate, refreshGenCosts]);

  useEffect(() => {
    loadInitialSettings();
  }, [loadInitialSettings]);

  const handleSaveCreditRate = async () => {
    const paise = Math.round(Number(creditRateDraft) * 100);
    if (!Number.isFinite(paise) || paise < 1) {
      setCreditRateError("Enter a valid amount (e.g. 1 = ₹1/credit)");
      return;
    }
    setSavingCreditRate(true);
    setCreditRateError("");
    try {
      const data = await subscriptionsService.adminSetCreditRate(paise);
      setCreditRate({ ...data, updated_at: new Date().toISOString() });
      setEditingCreditRate(false);
    } catch {
      setCreditRateError("Failed to save. Try again.");
    } finally {
      setSavingCreditRate(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    subscriptionsService
      .getTierLimits(role)
      .then((data) => {
        if (!cancelled) setTierLimits(data);
      })
      .catch(() => {
        if (!cancelled) setTierLimits(null);
      });
    return () => {
      cancelled = true;
    };
  }, [role]);

  const fetchCurrencies = useCallback(() => {
    let cancelled = false;
    const promise = subscriptionsService
      .adminGetCurrencies()
      .then((data) => {
        if (!cancelled) setCurrencies(data);
      })
      .catch(() => {
        if (!cancelled) setCurrencies(null);
      });
    return promise;
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([refresh(), refreshFeatureCatalog(), fetchCreditRate(), refreshGenCosts(), fetchCurrencies()]).then(() => {});
  }, [refresh, refreshFeatureCatalog, fetchCreditRate, refreshGenCosts, fetchCurrencies]);

  // One-time gate for the initial page load — the individual fetches above
  // each run on mount independently, but nothing surfaced a loading state
  // while they were in flight, so cards briefly rendered with blank/undefined
  // prices before data arrived.
  useEffect(() => {
    Promise.allSettled([refresh(), refreshFeatureCatalog(), fetchCreditRate(), refreshGenCosts(), fetchCurrencies()])
      .finally(() => setInitialLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartEdit = (tierKey: string) => {
    setEditingTier(tierKey);
    setDraftValue(String(tierLimits?.prices_credits?.[tierKey] ?? 0));
  };

  const handleSavePrice = async (tierKey: string) => {
    const credits = Math.round(Number(draftValue));
    if (!Number.isFinite(credits) || credits < 1) return;
    setSaving(true);
    try {
      await subscriptionsService.adminSetTierPriceCredits(role, tierKey, credits);
      setEditingTier(null);
      refresh();
    } finally {
      setSaving(false);
    }
  };

  const handleStartDiscountEdit = (tierKey: string) => {
    const existing = tierLimits?.pricing?.[tierKey]?.discount;
    setDiscountDraft({
      discount_type: existing?.type ?? "percent",
      value: existing ? String(existing.value) : "",
      starts_at: existing?.starts_at ? existing.starts_at.slice(0, 10) : "",
      ends_at: existing?.ends_at ? existing.ends_at.slice(0, 10) : "",
      active: existing?.active ?? true,
    });
    setDiscountError("");
    setDiscountEditingTier(tierKey);
  };

  const handleSaveDiscount = async (tierKey: string) => {
    const value = Math.round(Number(discountDraft.value));
    if (!Number.isFinite(value) || value < 0) {
      setDiscountError("Enter a valid non-negative number");
      return;
    }
    if (discountDraft.discount_type === "percent" && value > 100) {
      setDiscountError("Percent discount can't exceed 100");
      return;
    }
    const payload: TierDiscountRequest = {
      discount_type: discountDraft.discount_type,
      value,
      starts_at: discountDraft.starts_at || null,
      ends_at: discountDraft.ends_at || null,
      active: discountDraft.active,
    };
    setSavingDiscount(true);
    setDiscountError("");
    try {
      await subscriptionsService.adminSetTierDiscount(role, tierKey, payload);
      setDiscountEditingTier(null);
      refresh();
    } catch {
      setDiscountError("Failed to save. Try again.");
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleRemoveDiscount = async (tierKey: string) => {
    setSavingDiscount(true);
    try {
      await subscriptionsService.adminClearTierDiscount(role, tierKey);
      setDiscountEditingTier(null);
      refresh();
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleCurrencyChange = async (code: string) => {
    setSavingCurrency(true);
    try {
      await subscriptionsService.adminSetCurrency(code);
      const [cur] = await Promise.all([
        subscriptionsService.adminGetCurrencies(),
      ]);
      setCurrencies(cur);
      refresh();
    } finally {
      setSavingCurrency(false);
    }
  };

  const tierMetas = role === "seller" ? SELLER_TIERS : BUYER_TIERS;
  const tiers: TierWithFeatures[] = tierMetas.map((meta) => ({
    ...meta,
    features: buildFeatureRows(role, meta.key, tierLimits?.tiers?.[meta.key]),
  }));
  const cols = tiers.length === 4 ? "grid-cols-2 xl:grid-cols-4" : "grid-cols-2 xl:grid-cols-4";

  return (
    <div>
      <AdminPageHeader
        title="Credits & Features"
        subtitle="What each package includes, credit pricing and generation costs. Prices are set on Plans & Prices."
        actions={
          <>
            <RefreshButton onRefresh={handleRefreshAll} />
            {currencies && (
              <select
                value={currencies.active}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                disabled={savingCurrency}
                className="bg-gray-900 border border-gray-800 text-gray-300 text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
              >
                {currencies.currencies.map((c) => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.country}</option>
                ))}
              </select>
            )}
            <div className="flex bg-gray-900 rounded-xl p-1 gap-1">
              {(["seller", "buyer"] as const).map((r) => (
                <button key={r} onClick={() => setRole(r)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${role === r ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}>
                  {r}
                </button>
              ))}
            </div>
          </>
        }
      />

      {initialLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
          <Spinner />
        </div>
      ) : (
      <>
      {/* Cards */}
      <div className={`grid ${cols} gap-5 mb-8`}>
        {tiers.map((t) => (
          <TierCard
            key={t.key}
            tier={t}
            priceCredits={tierLimits?.prices_credits?.[t.key]}
            pricing={tierLimits?.pricing?.[t.key]}
            currencySymbol={activeCurrencySymbol}
            editable={t.key !== "free"}
            editing={editingTier === t.key}
            draftValue={draftValue}
            onStartEdit={() => handleStartEdit(t.key)}
            onChangeDraft={setDraftValue}
            onSave={() => handleSavePrice(t.key)}
            onCancel={() => setEditingTier(null)}
            saving={saving}
            discountEditing={discountEditingTier === t.key}
            discountDraft={discountDraft}
            onStartDiscountEdit={() => handleStartDiscountEdit(t.key)}
            onChangeDiscountDraft={(patch) => setDiscountDraft((prev) => ({ ...prev, ...patch }))}
            onSaveDiscount={() => handleSaveDiscount(t.key)}
            onCancelDiscount={() => { setDiscountEditingTier(null); setDiscountError(""); }}
            onRemoveDiscount={() => handleRemoveDiscount(t.key)}
            savingDiscount={savingDiscount}
            discountError={discountError}
          />
        ))}
      </div>

      {/* Comparison table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Feature Comparison</h2>
          <span className="text-xs text-gray-500">Monthly limits · daily caps apply additionally · 14-day free trial on all paid plans</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-800">
              <tr>
                <th className="text-left px-6 py-3 text-gray-500 font-medium w-44">Feature</th>
                {tiers.map((t) => (
                  <th key={t.key} className="text-center px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${t.badge}`}>{t.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {tiers[0].features.map((feat, fi) => (
                <tr key={feat.label} className="hover:bg-gray-800/20 transition-colors">
                  <td className="px-6 py-3 text-gray-400 text-xs flex items-center">
                    {feat.icon} {feat.label}
                    <FeatureInfoTooltip entry={feat.action ? featureCatalogByAction[feat.action] : undefined} />
                  </td>
                  {tiers.map((t) => {
                    const f = t.features[fi];
                    return (
                      <td key={t.key} className="text-center px-4 py-3">
                        <span className={`text-xs ${f.dim ? "text-gray-600 line-through" : f.green ? "text-green-400" : "text-white"}`}>
                          {f.value}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Feature info / tooltip catalog */}
      <div className="mt-6">
        <FeatureCatalogEditor entries={featureCatalog} onSaved={refreshFeatureCatalog} />
      </div>

      {/* Credit Rate */}
      <div className="mt-6 bg-gray-900 rounded-2xl border border-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Credit Rate</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              1 credit = how many paise. Controls boost wallet top-up and campaign budgets.
            </p>
          </div>
          {!editingCreditRate && (
            <button
              onClick={() => { setCreditRateDraft(String(creditRate?.rate_inr ?? 1)); setEditingCreditRate(true); setCreditRateError(""); }}
              className="text-xs px-3 py-1.5 rounded-lg text-indigo-400 hover:bg-indigo-500/10 border border-indigo-500/30"
            >
              Edit
            </button>
          )}
        </div>

        {editingCreditRate ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-gray-400 text-sm">1 credit =</span>
            <span className="text-gray-400 text-sm">₹</span>
            <input
              type="number"
              min={0.01}
              step={0.01}
              value={creditRateDraft}
              onChange={(e) => setCreditRateDraft(e.target.value)}
              className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
            <button
              onClick={() => void handleSaveCreditRate()}
              disabled={savingCreditRate}
              className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {savingCreditRate ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => { setEditingCreditRate(false); setCreditRateError(""); }}
              className="text-xs px-3 py-1.5 rounded-lg text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            {creditRateError && <span className="text-xs text-red-400">{creditRateError}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <span className="text-3xl font-bold text-white">
              ₹{creditRate?.rate_inr?.toFixed(2) ?? "—"}
            </span>
            <span className="text-gray-500 text-sm">per credit</span>
            <span className="text-gray-600 text-xs">({creditRate?.rate_paise ?? "—"} paise)</span>
            {creditRate?.updated_at && (
              <span className="ml-auto text-xs text-gray-600">
                Last updated {(parseInstant(creditRate.updated_at) ?? new Date(creditRate.updated_at)).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Per-generation pricing */}
      <div className="mt-6 bg-gray-900 rounded-2xl border border-gray-800 p-5">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-white">Per-Generation Pricing</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Credits charged from the wallet to generate one flyer / video / itinerary past the tier quota.
            The ₹ shown uses the current credit rate above.
          </p>
        </div>
        <div className="space-y-2">
          {(["flyer", "video", "itinerary"] as const).map((action) => {
            const credits = genCosts?.costs_credits?.[action];
            const inr = genCosts?.costs_inr?.[action];
            const isEditing = editingGenAction === action;
            return (
              <div key={action} className="flex items-center gap-3 py-1.5 border-b border-gray-800 last:border-0">
                <span className="w-24 text-sm text-gray-300 capitalize">{action}</span>
                {isEditing ? (
                  <>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={genDraft}
                      onChange={(e) => setGenDraft(e.target.value)}
                      className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      autoFocus
                    />
                    <span className="text-xs text-gray-500">credits</span>
                    <button
                      onClick={() => void handleSaveGenCost(action)}
                      disabled={savingGen}
                      className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {savingGen ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => { setEditingGenAction(null); setGenError(""); }}
                      className="text-xs px-3 py-1.5 rounded-lg text-gray-400 hover:text-white"
                    >
                      Cancel
                    </button>
                    {genError && <span className="text-xs text-red-400">{genError}</span>}
                  </>
                ) : (
                  <>
                    <span className="text-lg font-bold text-white w-20">{credits ?? "—"} cr</span>
                    <span className="text-sm text-gray-500">≈ ₹{inr?.toFixed(2) ?? "—"}</span>
                    <button
                      onClick={() => { setGenDraft(String(credits ?? 1)); setEditingGenAction(action); setGenError(""); }}
                      className="ml-auto text-xs px-3 py-1.5 rounded-lg text-indigo-400 hover:bg-indigo-500/10 border border-indigo-500/30"
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Boost info */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Feed Boost Multipliers</h3>
          <p className="text-xs text-gray-500 mb-3">Higher tiers get algorithmic ranking boosts in buyer feeds and recommendation engine (Rec Engine Spec §6.1).</p>
          <div className="space-y-2">
            {(role === "seller" ? [
              { tier: "Free", mult: "1.00×", badge: "bg-gray-700 text-gray-300" },
              { tier: "Starter", mult: "1.05×", badge: "bg-blue-900 text-blue-300" },
              { tier: "Mid", mult: "1.10×", badge: "bg-indigo-900 text-indigo-300" },
              { tier: "Premium", mult: "1.25×", badge: "bg-purple-900 text-purple-300" },
            ] : [
              { tier: "Free", mult: "1.00×", badge: "bg-gray-700 text-gray-300" },
              { tier: "Pro", mult: "1.05×", badge: "bg-teal-900 text-teal-300" },
              { tier: "Studio", mult: "1.10×", badge: "bg-cyan-900 text-cyan-300" },
              { tier: "Elite", mult: "1.25×", badge: "bg-amber-900 text-amber-300" },
            ]).map((row) => (
              <div key={row.tier} className="flex items-center justify-between">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${row.badge}`}>{row.tier}</span>
                <span className="text-white text-sm font-mono">{row.mult}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Lifecycle Rules</h3>
          <ul className="space-y-2 text-xs text-gray-400">
            <li className="flex gap-2"><span className="text-indigo-400 shrink-0">Trial</span> 14 days free on any paid plan. Phone on blocklist → no trial (paid directly).</li>
            <li className="flex gap-2"><span className="text-yellow-400 shrink-0">Past Due</span> Payment fails → past_due. Access retained during grace period (7 days).</li>
            <li className="flex gap-2"><span className="text-orange-400 shrink-0">Cancel</span> User keeps paid tier until end of billing period. Status = cancelled but access = granted.</li>
            <li className="flex gap-2"><span className="text-red-400 shrink-0">Expired</span> Only status that removes access. Tier reverts to free in all quota checks.</li>
            <li className="flex gap-2"><span className="text-green-400 shrink-0">Admin Grant</span> Layered on top of Razorpay state. Reverts automatically after grant_until date.</li>
          </ul>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
